import { captureException } from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import { useAccount, useConfig } from 'wagmi';
import { dataOrThrow, fetchClient } from '../lib/apiClient';
import { createOrderSubmitBody } from '../lib/createOrderSubmitBody';
import { isUserRejectedError } from '../lib/errors';
import { Gwei } from '../lib/gwei';
import { getOrderSubmitErrorMessage, ORDER_SUCCESS_RESULT } from '../lib/orderError';
import { startOrderTiming } from '../lib/orderTiming';
import { showToast } from '../lib/showToast';
import {
  createdOrderToToastMessage,
  createOrderCanceledToastMessage,
  createOrderFailedToastMessage,
} from '../lib/toasts';
import { assertRequiredData } from '../lib/typeGuards';
import { ValidatedOrder } from '../types/orders';
import { useDataForSigning } from './useDataForSigning';
import { useModalState } from './useModalState';
import { ProductType } from './useProducts';
import { useActiveSelectedSubaccount } from './useSelectedSubaccount';
import { useWalletStorage } from './useWalletStorage';

export const getOrderMutationKey = ({
  productId,
  closing,
}: {
  productId: ProductType['id'] | undefined;
  closing?: boolean;
}) => ['submitOrder', productId, closing].filter(Boolean);

export const useOrderMutation = (product: ProductType | undefined, closing?: boolean) => {
  const { getSubaccountData } = useWalletStorage();
  const selectedSubaccount = useActiveSelectedSubaccount();
  const { address } = useAccount();
  const { signedTypedDataTypes, domain } = useDataForSigning();
  const subaccountData = getSubaccountData(address, selectedSubaccount?.id);
  const config = useConfig();
  const setConfirmOrderModal = useModalState((state) => state.setConfirmOrderModal);

  return useMutation({
    mutationKey: getOrderMutationKey({ productId: product?.id, closing }),
    mutationFn: async (partialOrderData: ValidatedOrder) => {
      const params = {
        selectedSubaccount,
        partialOrderData,
        product,
        connectedAddress: address,
        domain,
        signedTypedDataTypes,
        config,
      };
      // We expect the place order button to be disabled if any of these are missing, but typescript isn't aware of that
      assertRequiredData(params);

      const { data } = await fetchClient
        .POST('/v1/order', {
          body: await createOrderSubmitBody({ ...params, subaccountData }),
        })
        .then(dataOrThrow);

      // Start timing for market orders
      if (
        params.partialOrderData.discriminator === 'SimpleMarketOrder' ||
        params.partialOrderData.discriminator === 'CloseMarketOrder'
      ) {
        startOrderTiming(data.id);
      }

      return { data, params };
    },
    onSuccess: ({ data, params }) => {
      // Check the result field to see if there's a non-OK status
      if (data.result === ORDER_SUCCESS_RESULT) {
        // close the confirm order modal if open
        setConfirmOrderModal('closed');

        const immediateFill =
          Gwei.toGwei(data.filled) === params.partialOrderData.amount ||
          params.partialOrderData.type === 'MARKET';

        // Only show success toast for non-filled and non-market orders
        if (product && !immediateFill) {
          showToast(createdOrderToToastMessage(product, params.partialOrderData));
        }
      } else {
        // For non-Ok results, show a toast with the appropriate message
        const errorDisplayMessage = getOrderSubmitErrorMessage(data.result, data);
        showToast(createOrderFailedToastMessage(errorDisplayMessage, Gwei.toGwei(data.filled)));
      }
    },
    onError: (error) => {
      if (isUserRejectedError(error)) {
        showToast(createOrderCanceledToastMessage());
        return;
      }

      console.error('Error placing order:', error);
      const errorMessage = Array.isArray(error.message) ? error.message[0] : error.message;
      console.error(errorMessage);

      const errorDisplayMessage = getOrderSubmitErrorMessage(errorMessage);
      if (!errorDisplayMessage) {
        captureException(error);
      }
      showToast(createOrderFailedToastMessage(errorDisplayMessage));
    },
  });
};
